"use client";

import { useState } from "react";
import { Menu, Plus } from "lucide-react";
import { usePathname } from "next/navigation";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { PayloadLink } from "@/components/shared/PayloadLink";
import { useNavCollapsed } from "./ScrollableHeader";
import { cn } from "@/lib/utils";
import { isNavItemActive, type NavItemConfig, type RightNavItem } from "@/lib/navigation-utils";

// Minimal shape shared by main and right nav items — all that the mobile
// stacked list needs to render a row.
type MobileNavLink = Pick<NavItemConfig, "id" | "label" | "type" | "page" | "url" | "newTab">
  | Pick<RightNavItem, "id" | "label" | "type" | "page" | "url" | "newTab">;

interface MobileNavProps {
  items?: MobileNavLink[];
}

export function MobileNav({ items }: MobileNavProps) {
  const [open, setOpen] = useState(false);
  const collapsed = useNavCollapsed();
  const pathname = usePathname() ?? "";

  if (!items || items.length === 0) return null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {!open && (
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className={cn(
            "xl:hidden fixed right-4 z-[100] grid place-items-center border border-white text-white transition-all duration-300",
            collapsed ? "top-4 h-9 w-9" : "top-[38px] h-11 w-11"
          )}
        >
          <Menu
            className={cn(
              "transition-all duration-300",
              collapsed ? "h-4 w-4" : "h-5 w-5"
            )}
          />
        </button>
      )}

      <SheetContent
        side="right"
        showCloseButton={false}
        className="w-[60%] bg-black text-white border-l-0 p-6 outline-none xl:hidden"
      >
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <nav aria-label="Primary" className="mt-2 flex flex-col">
          {items.map((item) => {
            const isActive = isNavItemActive(pathname, item);
            return (
              <PayloadLink
                key={item.id}
                type={item.type}
                page={item.page}
                url={item.url}
                newTab={item.newTab}
                onClick={() => setOpen(false)}
                className="group flex items-center gap-3 py-4 border-b border-white/10 transition-colors"
              >
                <Plus
                  className="h-5 w-5 text-white/80 transition-transform duration-300 group-hover:rotate-90"
                  aria-hidden="true"
                />
                <span
                  className={cn(
                    "font-semibold text-base leading-tight transition-colors group-hover:text-brand",
                    isActive ? "text-brand" : "text-white",
                  )}
                >
                  {item.label}
                </span>
              </PayloadLink>
            );
          })}
        </nav>

        {/*
          // --- Original 2-column icon grid layout (kept for future use) ---
          // Restore by swapping the <nav> above for this block and re-importing
          // NavIcon, Media, and the UIAssetLike alias.
          //
          // <nav aria-label="Primary" className="mt-1 grid grid-cols-2 gap-x-6 gap-y-8">
          //   {items.map((item, index) => {
          //     const itemWithIconAsset = item as NavItemConfig & {
          //       icon?: Media | string | number | null;
          //       iconAsset?: UIAssetLike;
          //     };
          //     const isWidow =
          //       items.length % 2 === 1 && index === items.length - 1;
          //     return (
          //       <PayloadLink
          //         key={item.id}
          //         type={item.type}
          //         page={item.page}
          //         url={item.url}
          //         newTab={item.newTab}
          //         onClick={() => setOpen(false)}
          //         className={`flex flex-col items-end gap-2 text-right transition-opacity hover:opacity-80${
          //           isWidow ? " col-start-2" : ""
          //         }`}
          //       >
          //         <NavIcon
          //           label={item.label}
          //           iconAsset={itemWithIconAsset.iconAsset}
          //           legacyIcon={itemWithIconAsset.icon}
          //           size={56}
          //           play={false}
          //         />
          //         <div className="flex flex-col items-end">
          //           <span className="text-white font-semibold text-base leading-tight">
          //             {item.label}
          //           </span>
          //           {item.description && (
          //             <span className="text-white/60 text-[10px] leading-tight max-w-[14ch] mt-1 text-right">
          //               {item.description}
          //             </span>
          //           )}
          //         </div>
          //       </PayloadLink>
          //     );
          //   })}
          // </nav>
        */}
      </SheetContent>
    </Sheet>
  );
}
